/**
 * Ganzzahlige Geldarithmetik. Keine Fliesskommazahlen, damit Client und
 * Worker bitgleich rechnen.
 *
 * Zwei Skalen:
 *   e4   — Stammdatenpreise in 1/10000 EUR je 100 kg.
 *   cent — Belegwerte in ganzen Cent. Alles Gedruckte.
 */

/**
 * Kaufmaennisch runden (half away from zero), rein ganzzahlig.
 *
 * Math.round() taugt hier nicht: es rundet Richtung +unendlich, also
 * Math.round(-0.5) === -0. Bei Abschlaegen und Gutschriften ist das falsch.
 */
export function roundDiv(zaehler: number, nenner: number): number {
  const halb = Math.trunc(nenner / 2);
  const wert =
    zaehler >= 0
      ? Math.trunc((zaehler + halb) / nenner)
      : -Math.trunc((-zaehler + halb) / nenner);
  // Der negative Zweig erzeugt bei kleinen Betraegen -0. Das rechnet zwar
  // richtig, ist aber !== 0 unter Object.is und taucht in JSON als -0 auf.
  // (-0) + 0 ist nach IEEE 754 genau +0.
  return wert + 0;
}

/** 1/10000 EUR → ganze Cent */
export const e4ToCent = (e4: number): number => roundDiv(e4, 100);

/** Anteil in Basispunkten: 300 bp = 3,00 % */
export const bpOf = (cent: number, bp: number): number => roundDiv(cent * bp, 10_000);

/**
 * Positionssumme: Preis je 100 kg × Menge in kg.
 *
 * Zwischenprodukt bleibt weit unter 2^53 — 4237 Cent × 25 000 kg ≈ 1,1e8.
 */
export const positionssumme = (endpreisCentJe100kg: number, mengeKg: number): number =>
  roundDiv(endpreisCentJe100kg * mengeKg, 100);

/** Cent → "1.234,56" (ohne Waehrungszeichen) */
export function formatCent(cent: number): string {
  return (cent / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** e4 → "38,5000" — fuer die Stammdatenpflege, wo Nachkommastellen zaehlen */
export function formatE4(e4: number): string {
  return (e4 / 10_000).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/** Basispunkte → "2,5" (ohne Prozentzeichen). 250 bp → "2,5" */
export function formatProzent(bp: number): string {
  return (bp / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** kg → "12,5" t */
export function formatTonnen(kg: number): string {
  return (kg / 1000).toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

/**
 * Vorzeichen mit typografischem Minus: 250 → "+0,03", −45000 → "−4,50".
 *
 * Eine gemeinsame Funktion, weil sonst jede Anzeigestelle ihre eigene baut:
 * Vor der Vereinheitlichung schrieb die Rechenwegtafel ein festes Plus voran
 * (aus −4,50 wurde „+−4,50") und die Preisfindungstabelle benutzte den
 * ASCII-Bindestrich statt U+2212.
 */
export function formatVorzeichen(e4: number, nachkomma = 2): string {
  const betrag = (Math.abs(e4) / 10_000).toLocaleString("de-DE", {
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  });
  return `${e4 >= 0 ? "+" : "−"}${betrag}`;
}

/** Alles, was jemand als Minus tippt oder einfügt, auf ASCII bringen. */
const MINUSZEICHEN = /[\u2212\u2012\u2013\u2014\u2015\uFE63\uFF0D]/g;

/**
 * Deutsche Eingabe → e4. "38,50" → 385000, "1.234,50" → 12345000.
 *
 * ACHTUNG: Der Punkt gilt als Tausendertrenner, nicht als Dezimalzeichen —
 * "38.50" ergibt 38 500 000, also 3.850,00 €. Das ist für de-DE richtig,
 * überrascht aber. NaN bei Unsinn.
 *
 * Typografische Minuszeichen werden normalisiert. Ohne das scheitert der
 * naheliegendste Weg zu einer negativen Prämie: den angezeigten Wert
 * markieren, kopieren, wieder einfügen. Die Anwendung DRUCKT U+2212, und
 * Number() kennt nur den ASCII-Bindestrich — Ergebnis wäre „Ungültige Zahl".
 */
export function parseEuroToE4(s: string): number {
  const n = Number(
    s.trim().replace(MINUSZEICHEN, "-").replace(/\./g, "").replace(",", "."),
  );
  return Number.isFinite(n) ? Math.round(n * 10_000) : Number.NaN;
}

/** "12,5" (Tonnen) → 12500 (kg) */
export function parseTonnenToKg(s: string): number {
  const n = Number(s.trim().replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 1000) : Number.NaN;
}
